import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, AlertTriangle, Heart, Shield } from 'lucide-react';
import { createAnimal, updateAnimal, deleteAnimal, getAnimalsByProperty } from '../services/animalApi';
import type { Animal, AnimalType, AnimalBehavior } from '../services/animalApi';
import { useI18n } from '../contexts/I18nContext';

interface AnimalManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
}

const AnimalManagementModal: React.FC<AnimalManagementModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyName,
}) => {
  const { t } = useI18n();
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState<Animal | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'dog' as AnimalType,
    breed: '',
    behavior: 'unknown' as AnimalBehavior,
    size: '',
    notes: '',
    is_dangerous: false,
  });

  useEffect(() => {
    if (isOpen && propertyId) {
      loadAnimals();
    }
  }, [isOpen, propertyId]);

  const loadAnimals = async () => {
    setIsLoading(true);
    try {
      const data = await getAnimalsByProperty(propertyId);
      setAnimals(data);
    } catch (error) {
      console.error('Erro ao carregar animais:', error);
      alert(t('animals.error.loading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (editingAnimal) {
        await updateAnimal(editingAnimal.id, formData);
      } else {
        await createAnimal({
          property_id: propertyId,
          ...formData,
        });
      }
      await loadAnimals();
      resetForm();
      alert(editingAnimal ? t('animals.success.updated') : t('animals.success.created'));
    } catch (error) {
      console.error('Erro ao salvar animal:', error);
      alert(t('animals.error.saving'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (animal: Animal) => {
    setEditingAnimal(animal);
    setFormData({
      name: animal.name,
      type: animal.type,
      breed: animal.breed || '',
      behavior: animal.behavior,
      size: animal.size || '',
      notes: animal.notes || '',
      is_dangerous: animal.is_dangerous,
    });
    setShowForm(true);
  };

  const handleDelete = async (animalId: string) => {
    if (!confirm(t('animals.confirmDelete'))) return;
    
    setIsLoading(true);
    try {
      await deleteAnimal(animalId);
      await loadAnimals();
      alert(t('animals.success.deleted'));
    } catch (error) {
      console.error('Erro ao excluir animal:', error);
      alert(t('animals.error.deleting'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'dog',
      breed: '',
      behavior: 'unknown',
      size: '',
      notes: '',
      is_dangerous: false,
    });
    setEditingAnimal(null);
    setShowForm(false);
  };

  const getAnimalTypeLabel = (type: AnimalType) => {
    const labels: Record<AnimalType, string> = {
      dog: t('animals.type.dog'),
      cat: t('animals.type.cat'),
      bird: t('animals.type.bird'),
      other: t('animals.type.other'),
    };
    return labels[type] || type;
  };

  const getBehaviorLabel = (behavior: AnimalBehavior) => {
    const labels: Record<AnimalBehavior, string> = {
      friendly: t('animals.behavior.friendly'),
      shy: t('animals.behavior.shy'),
      aggressive: t('animals.behavior.aggressive'),
      unknown: t('animals.behavior.unknown'),
    };
    return labels[behavior] || behavior;
  };

  const getBehaviorColor = (behavior: AnimalBehavior) => {
    const colors: Record<AnimalBehavior, string> = {
      friendly: '#10b981',
      shy: '#3b82f6',
      aggressive: '#ef4444',
      unknown: '#6b7280',
    };
    return colors[behavior] || '#6b7280';
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '30px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>{t('animals.title', { propertyName })}</h2>
            <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
              {t('animals.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '5px',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {!showForm ? (
          <>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{t('animals.total')}</strong> {animals.length}
                {animals.filter(a => a.is_dangerous).length > 0 && (
                  <span style={{ marginLeft: '10px', color: '#ef4444' }}>
                    <AlertTriangle size={16} style={{ display: 'inline', marginRight: '5px' }} />
                    {animals.filter(a => a.is_dangerous).length} {t('animals.dangerous')}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowForm(true)}
                style={{
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Plus size={16} />
                {t('animals.add')}
              </button>
            </div>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>{t('loading')}</div>
            ) : animals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Heart size={48} style={{ marginBottom: '10px', color: '#9333ea' }} />
                <p>{t('animals.none')}</p>
                <button
                  onClick={() => setShowForm(true)}
                  style={{
                    backgroundColor: '#9333ea',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '10px',
                  }}
                >
                  {t('animals.addFirst')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {animals.map((animal) => (
                  <div
                    key={animal.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '15px',
                      backgroundColor: animal.is_dangerous ? '#fef2f2' : '#f9fafb',
                      borderLeft: animal.is_dangerous ? '4px solid #ef4444' : '4px solid #10b981',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, color: '#333' }}>{animal.name}</h3>
                          {animal.is_dangerous && (
                            <span
                              style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <Shield size={12} />
                              {t('animals.dangerousTag')}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '14px', color: '#666' }}>
                          <span><strong>{t('animals.label.type')}</strong> {getAnimalTypeLabel(animal.type)}</span>
                          {animal.breed && <span><strong>{t('animals.label.breed')}</strong> {animal.breed}</span>}
                          <span>
                            <strong>{t('animals.label.behavior')}</strong>{' '}
                            <span style={{ color: getBehaviorColor(animal.behavior) }}>
                              {getBehaviorLabel(animal.behavior)}
                            </span>
                          </span>
                          {animal.size && <span><strong>{t('animals.label.size')}</strong> {animal.size}</span>}
                        </div>
                        {animal.notes && (
                          <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                            <strong>{t('animals.label.notes')}</strong> {animal.notes}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(animal)}
                          style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(animal.id)}
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <h3 style={{ margin: 0 }}>{editingAnimal ? t('animals.edit') : t('animals.new')}</h3>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.type')}</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as AnimalType })}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="dog">{t('animals.type.dog')}</option>
                <option value="cat">{t('animals.type.cat')}</option>
                <option value="bird">{t('animals.type.bird')}</option>
                <option value="other">{t('animals.type.other')}</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.breed')}</label>
              <input
                type="text"
                value={formData.breed}
                onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.behavior')}</label>
              <select
                value={formData.behavior}
                onChange={(e) => setFormData({ ...formData, behavior: e.target.value as AnimalBehavior })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="unknown">{t('animals.behavior.unknown')}</option>
                <option value="friendly">{t('animals.behavior.friendly')}</option>
                <option value="shy">{t('animals.behavior.shy')}</option>
                <option value="aggressive">{t('animals.behavior.aggressive')}</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.size')}</label>
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">{t('animals.size.select')}</option>
                <option value="pequeno">{t('animals.size.small')}</option>
                <option value="médio">{t('animals.size.medium')}</option>
                <option value="grande">{t('animals.size.large')}</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('animals.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="is_dangerous"
                checked={formData.is_dangerous}
                onChange={(e) => setFormData({ ...formData, is_dangerous: e.target.checked })}
              />
              <label htmlFor="is_dangerous" style={{ fontWeight: 'bold', color: '#ef4444' }}>
                <AlertTriangle size={16} style={{ display: 'inline', marginRight: '5px' }} />
                {t('animals.dangerousLabel')}
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  flex: 1,
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {isLoading ? t('saving') : editingAnimal ? t('actions.update') : t('actions.create')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  flex: 1,
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t('actions.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AnimalManagementModal;
